// utils/signatureUtils.ts
import { CreateAxiosDefaults } from 'axios';
import CryptoJS from 'crypto-js';

/**
 * 客户端 API 请求签名工具类
 * 实现了 HMAC-SHA1 签名机制，用于验证请求身份和完整性。
 */
export class SignatureUtils {
  private static readonly accessSecret = process.env.NEXT_PUBLIC_API_ACCESS_SECRET || '';
  private static readonly accessKey = 'askpaul_web'; // 固定的 access_key

  /**
   * 且使用标准 URI 编码 (encodeURIComponent)。
   */
  static _encode(value: string): string {
    if (value === null || value === undefined) return '';
    // 仅使用标准的 URI 编码，没有特殊替换
    return encodeURIComponent(String(value));
  }

  /**
   * 构造规范化的请求字符串（第1步）。
   * 按参数名的字典顺序排序，并用 & 连接，排除 signature 字段。
   * * @param params 包含所有查询参数（应已包含 access_key, timestamp, rid/sn）
   */
  static buildCanonicalQueryString(params: Record<string, any>): string {
    // 过滤掉 signature 字段（虽然此时 signature 应该还不存在）并按字典顺序排序参数名
    const sortedKeys = Object.keys(params)
      .filter(key => key !== 'signature')
      .sort();

    // 使用等号 = 连接 URL 编码后的参数名和参数值，然后用 & 连接所有参数对
    return sortedKeys
      .map(key => {
        const paramValue = params[key];
        // 确保参数值被正确处理，即使是 null/undefined，也按空字符串处理
        const value = paramValue === null || paramValue === undefined ? '' : String(paramValue);
        return `${key}=${value}`;
      })
      .join('&');

  }

  /**
   * 构造待签名字符串（第2步）。
   * 格式：HTTPMethod & percentEncode(path) & percentEncode(queryString) & percentEncode(body_data)
   * * @param method HTTP方法 (GET, POST, etc.)
   * @param path URL路径 (e.g., /v1/chat)
   * @param queryString 规范化请求字符串
   * @param bodyData POST/PUT/PATCH 请求的 Body 数据
   */
  static buildStringToSign(
    method: string,
    path: string,
    queryString: string,
    headersString: string,
    bodyData: any = {}
  ): string {
    const methodUpper = method.toUpperCase();
    // Path 必须编码
    const encodedPath = this._encode(path);

    // 对 QueryString 进行二次编码
    const encodedQueryString = this._encode(queryString);

    // 对 HeadersString 进行二次编码
    const encodedHeadersString = this._encode(headersString);

    // 处理请求体数据：POST/PUT/PATCH 等方法需要包含 Body JSON
    let bodyString = '';

    if (methodUpper === 'POST' || methodUpper === 'PUT' || methodUpper === 'PATCH') {
      // 假设 Body 是 JSON 对象，转换为 JSON 字符串
      bodyString = typeof bodyData === 'string' ? bodyData : (bodyData ? JSON.stringify(bodyData) : '');
    }
    const encodedBody = this._encode(bodyString); // 即使 Body 为空，也编码空字符串

    // 拼接顺序：Method & EncodedPath & EncodedQueryString & EncodedHeadersString & EncodedBody
    const signList = [
      methodUpper,
      encodedPath,
      encodedQueryString,
      encodedHeadersString
    ];

    if (bodyString && bodyString !== ' ' && bodyString !== '{}') {
      signList.push(encodedBody);
    }

    return signList.join('&');
  }

  /**
   * 计算 HMAC-SHA1 签名（第3步）。
   * * @param method HTTP方法
   * @param path URL路径
   * @param params 所有请求参数（在调用前应已包含 access_key, timestamp）
   * @param bodyData 请求体数据
   * @returns 全小写的 HMAC-SHA1 签名
   */
  static generateSignature(
    method: string,
    path: string,
    queryParams: Record<string, any>,
    headersParams: Record<string, string>,
    bodyData: any = {}
  ): string {
    // 运行时检查：确保密钥存在
    if (this.accessSecret === 'your_default_secret_here') {
      console.warn("API_ACCESS_SECRET 未配置！请检查环境变量。");
    }

    // 1. 构造规范化的请求字符串
    const canonicalQueryString = this.buildCanonicalQueryString(queryParams);
    const canonicalHeaders = this.buildCanonicalQueryString(headersParams);

    // 2. 构造待签名字符串
    const stringToSign = this.buildStringToSign(method, path, canonicalQueryString, canonicalHeaders, bodyData);
    // console.log("stringToSign:========1 ", stringToSign);
    // 3. 使用 HMAC-SHA1 算法计算签名
    const hmacSignature = CryptoJS.HmacSHA1(stringToSign, this.accessSecret);
    // console.log("hmacSignature:========2 ", hmacSignature.toString(CryptoJS.enc.Hex).toLowerCase());
    // 将签名结果转换为全小写的Hex格式，并返回
    return hmacSignature.toString(CryptoJS.enc.Hex).toLowerCase();
  }

  /**
   * 生成时间戳和随机数（防重放攻击）
   * @returns 包含 timestamp（精确到秒）和 rid（随机ID）的对象
   */
  static generateSecurityParams(config: CreateAxiosDefaults<any>): { access_key: string, timestamp: number, signature: string } {

    const _timestamp = Math.floor(Date.now() / 1000);

    // 准备queryString签名的参数
    const queryParams = {
      ...config.params,
      timestamp: _timestamp,
      // 可以添加其他必要参数
      access_key: this.accessKey, // 明确添加access_key参数
    };

    /**
     * 准备header签名
     * 添加Header中的包含OctoPaul字段
     */
    // @ts-ignore
    const headersParams = Object.keys(config.headers).reduce((acc, key) => {
      if (key.startsWith("OctoPaul")) {
        // @ts-ignore
        acc[key] = config.headers[key];
      }
      return acc;
    }, {} as Record<string, string>)


    // 生成HMAC-SHA1签名（签名内部已包含access_key前缀）
    const signature = this.generateSignature(
      config.method?.toUpperCase() || 'GET',
      '/api' + config.url || '',
      queryParams,
      headersParams,
      config.data
    );


    return {
      access_key: this.accessKey, // 确保 access_key 被一同返回
      timestamp: _timestamp, // 精确到秒的时间戳
      signature
    };
  }
}